import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hoobuy.boats';
const articleUrl = `${siteUrl}/guia-hoobuy-spreadsheet-2026`;

function ArticleStructuredData() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'HoobuySpreadsheet: O Guia Completo Que Ninguém Te Conta',
    description: 'Descubra como usar o HoobuySpreadsheet para economizar até 60% nas suas compras da China. Guia completo com dicas, comparações e estratégias verificadas.',
    url: articleUrl,
    author: { '@type': 'Organization', name: 'Equipe HooBuy Boats' },
    datePublished: '2026-06-01',
    dateModified: '2026-06-25',
    inLanguage: 'pt',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20desk%20setup%20with%20laptop%20displaying%20spreadsheet%2C%20warm%20cream%20background%2C%20natural%20soft%20daylight%2C%20organized%20workspace%2C%20clean%20editorial%20photography%2C%20sophisticated%20European%20aesthetic%2C%20gentle%20shadows%2C%20beige%20and%20cream%20palette&width=960&height=1200&seq=article-hero-v3&orientation=portrait',
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

export default function SeoArticlePage() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <ArticleStructuredData />
      <Navbar />

      <main>
        <article>
          {/* Hero Section */}
          <section className="relative flex flex-col lg:flex-row min-h-[70vh]">
            <div className="w-full lg:w-1/2 bg-background-50 flex items-center px-4 md:px-8 lg:px-16 py-24 lg:py-0">
              <div className="max-w-lg">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 font-label transition-colors duration-200 mb-6 cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t('back_home')}
                </Link>

                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
                  {t('article_title')}
                </h1>

                <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
                  {t('article_subtitle')}
                </p>

                <p className="text-xs text-foreground-400 font-label mb-8">
                  {t('article_meta')}
                </p>

                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="group inline-flex items-center gap-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-label font-semibold text-sm px-7 py-4 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <span>{t('article_cta')}</span>
                  <span className="w-7 h-7 flex items-center justify-center border border-background-50/40 rounded-full transition-transform duration-200 group-hover:translate-x-0.5">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </a>
              </div>
            </div>

            <div className="hidden lg:block w-1/2 bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20desk%20setup%20with%20laptop%20displaying%20spreadsheet%2C%20warm%20cream%20background%2C%20natural%20soft%20daylight%2C%20organized%20workspace%2C%20clean%20editorial%20photography%2C%20sophisticated%20European%20aesthetic%2C%20gentle%20shadows%2C%20beige%20and%20cream%20palette&width=960&height=1200&seq=article-hero-v3&orientation=portrait"
                alt="HoobuySpreadsheet guia completo"
                title="HoobuySpreadsheet guia completo de compras"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </section>

          {/* Article Content */}
          <section className="py-16 md:py-20 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              {/* Intro */}
              <p className="text-foreground-600 text-base leading-relaxed mb-10">
                {t('article_intro')}
              </p>

              {/* Section 1 */}
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                {t('article_section1_title')}
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-5">
                {t('article_section1_p1')}
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-12">
                {t('article_section1_p2')}
              </p>

              {/* CTA Mid-article */}
              <div className="bg-background-100 rounded-2xl p-8 md:p-10 mb-12 text-center">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
                  {t('article_cta')}
                </h3>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 bg-primary-500 text-background-50 dark:text-foreground-950 font-label font-semibold text-sm px-6 py-3 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer mt-4"
                >
                  <span>Hoobuy Sheet</span>
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>

              {/* Section 2 */}
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                {t('article_section2_title')}
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-5">
                {t('article_section2_p1')}
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-5">
                {t('article_section2_p2')}
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-12">
                {t('article_section2_p3')}
              </p>

              {/* Section 3 */}
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                {t('article_section3_title')}
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-5">
                {t('article_section3_p1')}
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-5">
                {t('article_section3_p2')}
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-12">
                {t('article_section3_p3')}
              </p>

              {/* Section 4 */}
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                {t('article_section4_title')}
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-5">
                {t('article_section4_p1')}
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-5">
                {t('article_section4_p2')}
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-12">
                {t('article_section4_p3')}
              </p>

              {/* EEAT Section */}
              <div className="bg-background-100 rounded-2xl p-8 md:p-10 mb-12">
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5 text-center">
                  EEAT — Expertise, Autoridade e Confiabilidade
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-user-star-line text-xl text-primary-500"></i>
                    </div>
                    <p className="text-sm text-foreground-600">{t('eeat_authority')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-eye-line text-xl text-primary-500"></i>
                    </div>
                    <p className="text-sm text-foreground-600">{t('eeat_transparency')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-database-2-line text-xl text-primary-500"></i>
                    </div>
                    <p className="text-sm text-foreground-600">{t('eeat_data')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-shield-check-line text-xl text-primary-500"></i>
                    </div>
                    <p className="text-sm text-foreground-600">{t('eeat_security')}</p>
                  </div>
                </div>
              </div>

              {/* Conclusion */}
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                {t('article_conclusion_title')}
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-5">
                {t('article_conclusion_p1')}
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-10">
                {t('article_conclusion_p2')}
              </p>

              {/* Back to home */}
              <div className="border-t border-background-200/70 pt-8">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-label font-semibold transition-colors duration-200 cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t('back_home')}
                </Link>
              </div>
            </div>
          </section>

          {/* CTA Bottom */}
          <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                {t('article_cta')}
              </h2>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="bg-primary-500 text-background-50 dark:text-foreground-950 font-label font-semibold text-sm px-7 py-4 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  {t('cta_hoobuy_sheet')}
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="border-2 border-foreground-200 text-foreground-700 font-label font-semibold text-sm px-7 py-4 rounded-full hover:border-primary-500 hover:text-primary-500 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  {t('cta_buy_now')}
                </a>
              </div>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}